import { create } from "zustand"
import { fetchConsolePrefs, pushConsolePrefs } from "@/core/user-prefs"

// Small persisted UI-preference store for the chrome. Initialized to defaults
// (so SSR + first client render match), then hydrated from localStorage on mount
// via hydrateShellPrefs() — same no-mismatch approach as the theme provider.
//
// The three VIEW-MENU prefs below (heroCompact, recordsFullscreen, enterToSend)
// are also synced to the user's GLOBAL account prefs (server-side), so they
// follow the user across browsers/devices. localStorage stays as a fast,
// offline cache for instant first paint; the server value (loaded in
// hydrateShellPrefs) wins when present. Theme is the same concern but lives in
// the theme provider (lib/theme.tsx). Per-section `collapsed`/`starred` stay
// browser-local — they're ephemeral, high-churn, and not "settings".
const HERO_KEY = "attend-console-hero-compact"
const RECORDS_FS_KEY = "attend-console-records-fullscreen"
const ENTER_SEND_KEY = "attend-console-enter-to-send"
const COLLAPSE_PREFIX = "attend-console-rail-collapsed:"
const STAR_PREFIX = "attend-console-channel-star:"

type ShellPrefs = {
  heroCompact: boolean
  setHeroCompact: (v: boolean) => void
  // Message composer: when true (default, Slack-style), Enter SENDS and
  // Shift+Enter inserts a newline. When false, Enter inserts a newline and
  // Cmd/Ctrl+Enter sends. Read live by the composers' keydown handler.
  enterToSend: boolean
  setEnterToSend: (v: boolean) => void
  // "Open records full-width": when true, records (a guest, a travel segment, a
  // session, a form…) open FULL-WIDTH instead of beside the list in a split
  // column. ColumnLayout reads this; the toggle lives in the top-bar View menu.
  recordsFullscreen: boolean
  setRecordsFullscreen: (v: boolean) => void
  // Per-section collapsed state for the left-rail accordions (channels, dms,
  // manage, …). Defaults to expanded (false) so SSR + first client render match
  // before hydrateShellPrefs() applies the stored choice.
  collapsed: Record<string, boolean>
  toggleCollapsed: (section: string) => void
  // Per-USER starred channels (this browser) — channel id → true. Surfaces a
  // "Starred" section at the top of the rail's channel area. Distinct from the
  // shared `pinnedAtTop` (which pins a channel for everyone).
  starred: Record<string, boolean>
  toggleStar: (channelId: string) => void
}

export const useShellPrefs = create<ShellPrefs>((set, get) => ({
  heroCompact: false,
  setHeroCompact: (v) => {
    try {
      localStorage.setItem(HERO_KEY, v ? "1" : "0")
    } catch {
      // ignore (private mode etc.)
    }
    set({ heroCompact: v })
    pushConsolePrefs({ heroCompact: v })
  },
  recordsFullscreen: false,
  setRecordsFullscreen: (v) => {
    try {
      localStorage.setItem(RECORDS_FS_KEY, v ? "1" : "0")
    } catch {
      // ignore (private mode etc.)
    }
    set({ recordsFullscreen: v })
    pushConsolePrefs({ recordsFullscreen: v })
  },
  enterToSend: true,
  setEnterToSend: (v) => {
    try {
      localStorage.setItem(ENTER_SEND_KEY, v ? "1" : "0")
    } catch {
      // ignore (private mode etc.)
    }
    set({ enterToSend: v })
    pushConsolePrefs({ enterToSend: v })
  },
  collapsed: {},
  toggleCollapsed: (section) => {
    const next = !get().collapsed[section]
    try {
      localStorage.setItem(COLLAPSE_PREFIX + section, next ? "1" : "0")
    } catch {
      // ignore (private mode etc.)
    }
    set((s) => ({ collapsed: { ...s.collapsed, [section]: next } }))
  },
  starred: {},
  toggleStar: (channelId) => {
    const next = !get().starred[channelId]
    try {
      if (next) localStorage.setItem(STAR_PREFIX + channelId, "1")
      else localStorage.removeItem(STAR_PREFIX + channelId)
    } catch {
      // ignore (private mode etc.)
    }
    set((s) => ({ starred: { ...s.starred, [channelId]: next } }))
  },
}))

// Read persisted prefs into the store. Call once on the client after mount.
export function hydrateShellPrefs() {
  if (typeof window === "undefined") return
  try {
    const collapsed: Record<string, boolean> = {}
    const starred: Record<string, boolean> = {}
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key?.startsWith(COLLAPSE_PREFIX)) {
        collapsed[key.slice(COLLAPSE_PREFIX.length)] = localStorage.getItem(key) === "1"
      } else if (key?.startsWith(STAR_PREFIX)) {
        starred[key.slice(STAR_PREFIX.length)] = localStorage.getItem(key) === "1"
      }
    }
    useShellPrefs.setState({
      heroCompact: localStorage.getItem(HERO_KEY) === "1",
      recordsFullscreen: localStorage.getItem(RECORDS_FS_KEY) === "1",
      // Default ON unless explicitly disabled.
      enterToSend: localStorage.getItem(ENTER_SEND_KEY) !== "0",
      collapsed,
      starred,
    })
  } catch {
    // ignore
  }

  // Then overlay the account-synced values (server is the global source of
  // truth for these three; localStorage above was just the instant-paint
  // cache). Best-effort: fetchConsolePrefs never throws — if the user is
  // signed out or the endpoint isn't deployed yet, this is a no-op and the
  // local values stand. Each applied value is written back to localStorage so
  // the next cold start paints the synced choice immediately.
  void fetchConsolePrefs().then((p) => {
    const patch: { heroCompact?: boolean; recordsFullscreen?: boolean; enterToSend?: boolean } = {}
    if (typeof p.heroCompact === "boolean") {
      patch.heroCompact = p.heroCompact
      try {
        localStorage.setItem(HERO_KEY, p.heroCompact ? "1" : "0")
      } catch {
        // ignore
      }
    }
    if (typeof p.recordsFullscreen === "boolean") {
      patch.recordsFullscreen = p.recordsFullscreen
      try {
        localStorage.setItem(RECORDS_FS_KEY, p.recordsFullscreen ? "1" : "0")
      } catch {
        // ignore
      }
    }
    if (typeof p.enterToSend === "boolean") {
      patch.enterToSend = p.enterToSend
      try {
        localStorage.setItem(ENTER_SEND_KEY, p.enterToSend ? "1" : "0")
      } catch {
        // ignore
      }
    }
    if (Object.keys(patch).length) useShellPrefs.setState(patch)
  })
}
